import type { PrequalSubmissionCompletionMode } from "@/api/prequalification";

/** The completion facts a submission and a buyer's prequalification link both
 *  carry (the one-call brief, ruling Q4). */
export interface PrequalCompletionFacts {
  completionMode?: PrequalSubmissionCompletionMode | null;
  certifiedByName?: string | null;
  /** An INSTANT — the moment the consultant certified, not a picked day. */
  certifiedAtUtc?: string | null;
}

/**
 * "Completed by phone — certified by {name}, {date}" for a prequalification a
 * Buyer Consultant took on a telephone call; null for every other completion
 * mode, which says nothing.
 *
 * <p>🔴 <b>`formatInstant` must be the viewer's tz-aware formatter</b>
 * (`useDateFormatter().date`). `certifiedAtUtc` is a clock stamp: rendered in
 * UTC, a certification given after 5pm in Phoenix reads as the next day.</p>
 *
 * <p>A missing name or date drops its part rather than printing a blank or a
 * placeholder: the sentence still says the call happened and was certified,
 * which is the fact that matters.</p>
 */
export function phoneCertificationLine(
  facts: PrequalCompletionFacts | null | undefined,
  formatInstant: (iso: string) => string,
): string | null {
  if (facts?.completionMode !== "StaffCertifiedByPhone") return null;
  const name = facts.certifiedByName?.trim() ?? "";
  // The formatter returns "" for a value it cannot parse.
  const when = facts.certifiedAtUtc ? formatInstant(facts.certifiedAtUtc) : "";
  if (name && when) return `Completed by phone — certified by ${name}, ${when}`;
  if (name) return `Completed by phone — certified by ${name}`;
  if (when) return `Completed by phone — certified on ${when}`;
  return "Completed by phone";
}
